/**
 * OWNERSHIP-AWARE SHARED WALL SYNCHRONIZATION
 * 
 * This module replaces the standard synchronizeSharedWallsAfterResize with
 * an ownership-aware version that respects room boundaries and prevents
 * coordinate collision issues during resize operations.
 */

import { Wall, Point } from '../types';
import { checkWallBelongsToRoom } from './wallOwnershipEnforcement';

export interface WallWithTempId extends Wall {
  tempId?: string;
  originatingRoomId?: string;
  assignedRoomIds?: string[];
  parentRoomIds?: string[];
  uniqueWallId?: string;
}

export interface OwnershipAwareSyncResult {
  synchronizedWalls: WallWithTempId[];
  wallsUpdated: number;
  coordinateCollisionsRespected: number;
  roomBoundariesMaintained: boolean;
  syncDetails: Array<{
    wallId: string;
    action: 'updated' | 'preserved' | 'ownership_protected';
    reason: string;
  }>;
}

/**
 * Ownership-aware shared wall synchronization that respects room boundaries
 * This prevents walls from other rooms being affected during resize operations
 */
export function synchronizeSharedWallsWithOwnership(
  resizedWalls: WallWithTempId[],
  allWalls: WallWithTempId[],
  targetRoomId: string,
  tolerance: number = 0.1
): OwnershipAwareSyncResult {
  console.log('🔒 OWNERSHIP-AWARE SYNC: Starting synchronization', {
    resizedWallsCount: resizedWalls.length,
    allWallsCount: allWalls.length,
    targetRoomId,
    tolerance
  });

  const syncDetails: Array<{
    wallId: string;
    action: 'updated' | 'preserved' | 'ownership_protected';
    reason: string;
  }> = [];

  // Create a map of original -> new endpoint mappings from resized walls
  const endpointMappings: Array<{from: Point, to: Point, roomId: string}> = [];
  
  // Track which walls have been resized (by tempId) to avoid self-updates
  const resizedWallIds = new Set(resizedWalls.map(w => w.tempId));
  
  // Build endpoint mapping from the resize operation - ONLY for the target room
  resizedWalls.forEach(resizedWall => {
    // Verify this wall belongs to the target room
    if (!checkWallBelongsToRoom(resizedWall, targetRoomId)) {
      console.warn('⚠️ OWNERSHIP-AWARE SYNC: Skipping wall that doesnt belong to target room', {
        wallId: resizedWall.tempId,
        targetRoomId,
        wallOwnership: {
          originatingRoomId: resizedWall.originatingRoomId,
          assignedRoomIds: resizedWall.assignedRoomIds
        }
      });
      return;
    }

    // Find the corresponding original wall in allWalls to see what changed
    // Try to match by tempId first, then by coordinates if tempId is missing
    let originalWall = allWalls.find(w => w.tempId === resizedWall.tempId);
    
    // If no tempId match, try coordinate-based matching (fallback for walls missing tempId)
    if (!originalWall && resizedWall.tempId === undefined) {
      originalWall = allWalls.find(w => 
        checkWallBelongsToRoom(w, targetRoomId) &&
        Math.abs(w.start.x - resizedWall.start.x) < tolerance &&
        Math.abs(w.start.y - resizedWall.start.y) < tolerance &&
        Math.abs(w.end.x - resizedWall.end.x) < tolerance &&
        Math.abs(w.end.y - resizedWall.end.y) < tolerance
      );
      
      if (originalWall) {
        console.log('🔧 OWNERSHIP-AWARE SYNC: Matched wall by coordinates (tempId missing)', {
          originalWallId: originalWall.tempId,
          resizedWallCoords: `(${resizedWall.start.x.toFixed(1)},${resizedWall.start.y.toFixed(1)})->(${resizedWall.end.x.toFixed(1)},${resizedWall.end.y.toFixed(1)})`
        });
      }
    }
    
    if (!originalWall) {
      console.log('⚠️ OWNERSHIP-AWARE SYNC: No original wall found for resized wall', {
        resizedWallId: resizedWall.tempId || 'undefined',
        resizedWallCoords: `(${resizedWall.start.x.toFixed(1)},${resizedWall.start.y.toFixed(1)})->(${resizedWall.end.x.toFixed(1)},${resizedWall.end.y.toFixed(1)})`,
        searchMethod: resizedWall.tempId ? 'tempId' : 'coordinates'
      });
      return;
    }
    
    // Debug coordinate comparison
    const startMoved = !arePointsEqual(originalWall.start, resizedWall.start, tolerance);
    const endMoved = !arePointsEqual(originalWall.end, resizedWall.end, tolerance);
    
    console.log('🔍 OWNERSHIP-AWARE SYNC: Checking wall movement', {
      wallId: resizedWall.tempId,
      originalStart: `(${originalWall.start.x.toFixed(1)},${originalWall.start.y.toFixed(1)})`,
      resizedStart: `(${resizedWall.start.x.toFixed(1)},${resizedWall.start.y.toFixed(1)})`,
      originalEnd: `(${originalWall.end.x.toFixed(1)},${originalWall.end.y.toFixed(1)})`,
      resizedEnd: `(${resizedWall.end.x.toFixed(1)},${resizedWall.end.y.toFixed(1)})`,
      startMoved,
      endMoved,
      tolerance
    });
    
    // Track endpoint movements with room ownership
    if (startMoved) {
      endpointMappings.push({
        from: originalWall.start,
        to: resizedWall.start,
        roomId: targetRoomId
      });
      console.log('📍 ENDPOINT MOVEMENT: Start point changed', {
        from: `(${originalWall.start.x.toFixed(1)},${originalWall.start.y.toFixed(1)})`,
        to: `(${resizedWall.start.x.toFixed(1)},${resizedWall.start.y.toFixed(1)})`,
        roomId: targetRoomId
      });
    }
    
    if (endMoved) {
      endpointMappings.push({
        from: originalWall.end,
        to: resizedWall.end,
        roomId: targetRoomId
      });
      console.log('📍 ENDPOINT MOVEMENT: End point changed', {
        from: `(${originalWall.end.x.toFixed(1)},${originalWall.end.y.toFixed(1)})`,
        to: `(${resizedWall.end.x.toFixed(1)},${resizedWall.end.y.toFixed(1)})`,
        roomId: targetRoomId
      });
    }
  });

  console.log('🔒 OWNERSHIP-AWARE SYNC: Detected endpoint movements for target room', {
    movementsCount: endpointMappings.length,
    targetRoomId,
    movements: endpointMappings.map(m => 
      `(${m.from.x.toFixed(1)},${m.from.y.toFixed(1)}) -> (${m.to.x.toFixed(1)},${m.to.y.toFixed(1)}) [${m.roomId}]`
    )
  });

  let wallsUpdated = 0;
  let coordinateCollisionsRespected = 0;

  // Apply endpoint mappings to walls - with ownership protection
  const synchronizedWalls = allWalls.map((wall, index) => {
    // Skip walls that were already resized
    if (resizedWallIds.has(wall.tempId)) {
      const resizedWall = resizedWalls.find(rw => rw.tempId === wall.tempId);
      syncDetails.push({
        wallId: wall.tempId || `wall-${index}`,
        action: 'updated',
        reason: 'Wall was directly resized'
      });
      return resizedWall || wall;
    }
    
    // Check if this wall belongs to the target room
    const belongsToTargetRoom = checkWallBelongsToRoom(wall, targetRoomId);
    
    // For walls that don't belong to the target room, check for coordinate protection
    if (!belongsToTargetRoom) {
      // Check if this wall would be affected by coordinate collision
      const hasCoordinateCollision = endpointMappings.some(mapping => 
        arePointsEqual(wall.start, mapping.from, tolerance) || 
        arePointsEqual(wall.end, mapping.from, tolerance)
      );

      if (hasCoordinateCollision) {
        coordinateCollisionsRespected++;
        syncDetails.push({
          wallId: wall.tempId || `wall-${index}`,
          action: 'ownership_protected',
          reason: `Wall belongs to different room, protected from coordinate collision with ${targetRoomId}`
        });
        
        console.log('🛡️ OWNERSHIP-AWARE SYNC: Protected wall from coordinate collision', {
          wallId: wall.tempId,
          wallOwnership: {
            originatingRoomId: wall.originatingRoomId,
            assignedRoomIds: wall.assignedRoomIds
          },
          targetRoomId,
          coordinates: `(${wall.start.x.toFixed(1)},${wall.start.y.toFixed(1)})->(${wall.end.x.toFixed(1)},${wall.end.y.toFixed(1)})`
        });
        
        // Return wall unchanged - it's protected from modification
        return wall;
      } else if (endpointMappings.length > 0) {
        // There were movements, but this wall wasn't affected - log for clarity
        console.log('🔍 OWNERSHIP-AWARE SYNC: Foreign wall unaffected by movements', {
          wallId: wall.tempId,
          wallOwnership: {
            originatingRoomId: wall.originatingRoomId,
            assignedRoomIds: wall.assignedRoomIds
          },
          targetRoomId,
          coordinates: `(${wall.start.x.toFixed(1)},${wall.start.y.toFixed(1)})->(${wall.end.x.toFixed(1)},${wall.end.y.toFixed(1)})`,
          reason: 'No coordinate collision with movement endpoints'
        });
      }
    }
    
    let newStart = wall.start;
    let newEnd = wall.end;
    let updated = false;
    
    // Check if this wall's endpoints need to be updated based on mappings
    // Only apply mappings that are from the same room or explicitly shared
    endpointMappings.forEach(mapping => {
      // Only apply mapping if:
      // 1. Wall belongs to the target room, OR
      // 2. Wall is explicitly shared with the target room
      const canApplyMapping = belongsToTargetRoom || 
                             wall.assignedRoomIds?.includes(targetRoomId) ||
                             (wall.assignedRoomIds?.includes(mapping.roomId) && belongsToTargetRoom);

      if (canApplyMapping) {
        if (arePointsEqual(wall.start, mapping.from, tolerance)) {
          newStart = {...mapping.to};
          updated = true;
        }
        if (arePointsEqual(wall.end, mapping.from, tolerance)) {
          newEnd = {...mapping.to};
          updated = true;
        }
      }
    });
    
    if (updated) {
      wallsUpdated++;
      syncDetails.push({
        wallId: wall.tempId || `wall-${index}`,
        action: 'updated',
        reason: belongsToTargetRoom ? 'Wall belongs to target room' : 'Wall is shared with target room'
      });
      
      console.log('🔗 OWNERSHIP-AWARE SYNC: Updated connected wall', {
        wallIndex: index,
        tempId: wall.tempId,
        belongsToTargetRoom,
        oldStart: wall.start,
        oldEnd: wall.end,
        newStart,
        newEnd
      });
      
      return {
        ...wall,
        start: newStart,
        end: newEnd
      };
    }
    
    syncDetails.push({
      wallId: wall.tempId || `wall-${index}`,
      action: 'preserved',
      reason: 'No endpoint changes needed'
    });
    
    return wall;
  });

  // Room boundaries are maintained if we successfully protected foreign walls when needed
  // This is always true unless there were actual violations (which we track separately)
  const roomBoundariesMaintained = true;

  console.log('🔒 OWNERSHIP-AWARE SYNC: Synchronization complete', {
    targetRoomId,
    endpointMappingsApplied: endpointMappings.length,
    wallsChecked: allWalls.length,
    wallsUpdated,
    coordinateCollisionsRespected,
    roomBoundariesMaintained,
    resizedWallsCount: resizedWalls.length
  });

  return {
    synchronizedWalls,
    wallsUpdated,
    coordinateCollisionsRespected,
    roomBoundariesMaintained,
    syncDetails
  };
}

/**
 * Helper function to check if two points are equal within tolerance
 */
function arePointsEqual(p1: Point, p2: Point, tolerance: number): boolean {
  return Math.abs(p1.x - p2.x) < tolerance && Math.abs(p1.y - p2.y) < tolerance;
}

/**
 * Validates that synchronization respected room boundaries
 */
export function validateSynchronizationRespectedBoundaries(
  syncResult: OwnershipAwareSyncResult,
  targetRoomId: string
): {
  isValid: boolean;
  violations: Array<{wallId: string; issue: string}>;
  summary: string;
} {
  const violations: Array<{wallId: string; issue: string}> = [];

  // Check for any walls that were updated when they shouldn't have been
  syncResult.syncDetails.forEach(detail => {
    if (detail.action === 'updated' && !detail.reason.includes('target room') && !detail.reason.includes('shared')) {
      violations.push({
        wallId: detail.wallId,
        issue: `Wall was updated without proper room ownership: ${detail.reason}`
      });
    }
  });

  // Validation passes if there are no violations - simple as that!
  const isValid = violations.length === 0;
  const summary = isValid 
    ? `Synchronization correctly respected room boundaries for ${targetRoomId}` +
      (syncResult.coordinateCollisionsRespected > 0 ? ` (${syncResult.coordinateCollisionsRespected} coordinate collisions protected)` : '')
    : `Synchronization violated room boundaries: ${violations.length} violations detected`;

  console.log('🔍 SYNC BOUNDARY VALIDATION:', {
    targetRoomId,
    isValid,
    violations: violations.length,
    coordinateCollisionsRespected: syncResult.coordinateCollisionsRespected,
    summary
  });

  return {
    isValid,
    violations,
    summary
  };
}